import { supabase } from '@/integrations/supabase/client';
import { userFacingErrorMessage, userFacingFailureText } from '@/utils/userFacingError';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

export interface OnboardingData {
  organizationNumber: string;
  companyName: string;
}

export interface OnboardingResponse {
  success: boolean;
  companyId?: string;
  error?: string;
}

export interface OnboardingStatusResponse {
  success: boolean;
  completed?: boolean;
  completed_erp_connection?: boolean;
  completed_bank_connection?: boolean;
  completed_kyc?: boolean;
  completed_company_context?: boolean;
  error?: string;
}

export interface CompanyContextResponse {
  success: boolean;
  contextText?: string;
  error?: string;
}

export interface SaveCompanyContextPayload {
  contextText: string;
  /** The recurring-cadence label shown in the form (e.g. "Kalenderår (jan–dec)"). */
  fiscalYear: string;
  /** broken-fiscal-year.AC1: the company's actual first-year dates — not derived from `fiscalYear`. */
  fiscalYearStartDate: string;
  fiscalYearEndDate: string;
  accountingMethod: string;
  companyType: string;
  /** Persisted to companies.vat_registered (#287) — false is a valid "momsbefriad" state, not missing data. */
  vatRegistered: boolean;
  /** Persisted to companies.f_skatt (#287) — a real column, not free text. */
  fSkatt: boolean;
}

export const onboardingApiClient = {
  async getStatus(): Promise<OnboardingStatusResponse>{
    try {
      const { data: response, error } = await supabase.functions.invoke('onboarding-api', {
        body: { action: 'status' }
      });
      if (error) return { success: false, error: await userFacingFailureText(error, translateOutsideReact('consultantErrors.fallback.onboardingStatusLoad')) };
      return {
        success: true,
        completed: response?.completed === true,
        completed_erp_connection: response?.completed_erp_connection === true,
        completed_bank_connection: response?.completed_bank_connection === true,
        completed_kyc: response?.completed_kyc === true,
        completed_company_context: response?.completed_company_context === true,
      };
    } catch (err) {
      return { success: false, error: userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.onboardingStatusLoad')) };
    }
  },
  async onboardCompany(data: OnboardingData): Promise<OnboardingResponse> {
    try {
      // Call the onboarding edge function
      const { data: response, error } = await supabase.functions.invoke('onboarding-api', {
        body: {
          action: 'create_company',
          organizationNumber: data.organizationNumber,
          companyName: data.companyName
        }
      });

      if (error) {
        return { success: false, error: await userFacingFailureText(error, translateOutsideReact('consultantErrors.fallback.companyCreate')) };
      }

      if (!response?.success) {
        return { success: false, error: response?.error || 'Onboarding failed' };
      }

      return { success: true, companyId: response?.companyId };
    } catch (err) {
      return {
        success: false,
        error: userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.companyCreate'))
      };
    }
  },

  async updateKycCompletion(completed: boolean): Promise<OnboardingResponse> {
    try {
      const { data: response, error } = await supabase.functions.invoke('onboarding-api', {
        body: {
          action: 'update_kyc_completion',
          kycCompleted: completed
        }
      });

      if (error) {
        return { success: false, error: await userFacingFailureText(error, translateOutsideReact('consultantErrors.fallback.kycStepSave')) };
      }

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.kycStepSave'))
      };
    }
  },

  async getCompanyContext(): Promise<CompanyContextResponse> {
    try {
      const { data: response, error } = await supabase.functions.invoke('onboarding-api', {
        body: { action: 'get_company_context' }
      });
      if (error) return { success: false, error: await userFacingFailureText(error, translateOutsideReact('consultantErrors.fallback.companyContextLoad')) };
      return { success: true, contextText: response?.contextText ?? '' };
    } catch (err) {
      return { success: false, error: userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.companyContextLoad')) };
    }
  },

  async saveCompanyContext(payload: SaveCompanyContextPayload): Promise<OnboardingResponse> {
    try {
      const { data: response, error } = await supabase.functions.invoke('onboarding-api', {
        body: { action: 'save_company_context', ...payload }
      });
      if (error) return { success: false, error: await userFacingFailureText(error, translateOutsideReact('consultantErrors.fallback.companyContextSave')) };
      if (!response?.success) return { success: false, error: response?.error || 'Save failed' };
      return { success: true };
    } catch (err) {
      return { success: false, error: userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.companyContextSave')) };
    }
  },

  async updateErpConnectionCompletion(completed: boolean): Promise<OnboardingResponse> {
    try {
      const { data: response, error } = await supabase.functions.invoke('onboarding-api', {
        body: {
          action: 'update_erp_connection_completion',
          erpConnectionCompleted: completed
        }
      });

      if (error) {
        return { success: false, error: await userFacingFailureText(error, translateOutsideReact('consultantErrors.fallback.erpStepSave')) };
      }

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.erpStepSave'))
      };
    }
  }
}; 